import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/** Outer pixel size of the FAB: ShibaTeacher 48 + p-2 (8px ×2) + border-2 (2px ×2). */
export const FAB_ICON_SIZE = 68;
/** Distance from the snapped side edge — matches the original `left-4 / right-4` (1rem). */
export const FAB_EDGE_MARGIN = 16;
/** Approx. BottomNav height incl. padding + safe-area; the FAB never overlaps the nav. */
export const FAB_NAV_H = 64;
/** Top safe-area inset; the FAB never goes above this. */
export const FAB_TOP_INSET = 16;
/**
 * Default vertical ratio. ≈ the original `bottom-24` (6rem above the nav) on a
 * typical phone (e.g. 812px tall: top ≈ (812 - 96 - 68) / 812 ≈ 0.8). Tunable —
 * the spec does not lock pixel values.
 */
export const DEFAULT_OFFSET_Y_RATIO = 0.8;

export interface SnapViewport {
  width: number;
  height: number;
  navH: number;
  iconH: number;
  topInset: number;
}

/**
 * Pure. Given the icon-center X and icon-top Y at drag release, decide which
 * side to snap to and the vertical ratio to persist (clamped to the safe band
 * between the top inset and just above the bottom nav). No side effects.
 *
 * - Horizontal: x < width/2 → "left", else "right" (exact midline → "right").
 * - Vertical: clamp y to [topInset, height - navH - iconH], divide by height.
 */
export function resolveSnap(
  x: number,
  y: number,
  vp: SnapViewport,
): { side: "left" | "right"; offsetYRatio: number } {
  const side: "left" | "right" = x < vp.width / 2 ? "left" : "right";
  const maxTop = Math.max(vp.topInset, vp.height - vp.navH - vp.iconH);
  const clampedY = Math.min(Math.max(y, vp.topInset), maxTop);
  const offsetYRatio = vp.height > 0 ? clampedY / vp.height : 0;
  return { side, offsetYRatio };
}

export interface CoachFabState {
  side: "left" | "right";
  offsetYRatio: number;
  setPosition: (side: "left" | "right", offsetYRatio: number) => void;
}

export const useCoachFabStore = create<CoachFabState>()(
  persist(
    (set) => ({
      side: "right",
      offsetYRatio: DEFAULT_OFFSET_Y_RATIO,
      setPosition: (side, offsetYRatio) => set({ side, offsetYRatio }),
    }),
    {
      name: "seiwa.coachfab.v1",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
